import { useEffect, useRef } from 'react';
import useSchools from '../../hooks/useSchools';
import TranscriptDetailsAccordion from './TranscriptDetailsAccordion';
import { getTranscriptDetails } from '../../../services/transcriptsService';
import TranscriptDetailsHeader from './TranscriptDetailsHeader';
import CustomButton from '../../reusable/CustomButton';
import { useTranscriptSelectionOutletContext } from '../../hooks/useTranscriptSelectionOutletContext';
import useTranscriptSelectionPaginationData from '../../hooks/useTranscriptSelectionPaginationData';
import { getTranscriptSelectionSummary } from '../../../services/transcriptsService';
import useUserDetails from '../../hooks/useUserDetails';
import { checkForLink } from '../utilityFunctions';
import type { Transcript, TranscriptDetails as TranscriptDetailsType } from '../../../interfaces/TranscriptsInterface';
import { Navigate } from 'react-router-dom';
import { NAVIGATION_LINKS_DATA } from '../../../constants';
import WithDataWrapper from '../../reusable/WithDataWrapper';

import './TranscriptDetails.css';

interface PageBuffer {
  transcripts: Transcript[];
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface TranscriptDetailsProps {
  transcriptDetails: TranscriptDetailsType;
  previewTranscript: Transcript;
  previousPage: React.MutableRefObject<PageBuffer | null>;
  nextPage: React.MutableRefObject<PageBuffer | null>;
}

function TranscriptDetails({ transcriptDetails, previewTranscript, previousPage, nextPage }: TranscriptDetailsProps) {
  const {
    previewTranscriptIndex,
    setPreviewTranscriptIndex,
    detailsTranscripts,
    detailsCategory,
    detailsLinks,
    updateDetailsTranscripts,
    updateDetailsLinks,
  } = useTranscriptSelectionOutletContext();

  const { lastNavigatedTranscriptSelectionPage } = useTranscriptSelectionPaginationData();

  let isFirstTranscriptInCategory = previewTranscriptIndex === 0;
  let isLastTranscriptInCategory = previewTranscriptIndex === detailsTranscripts.length - 1;

  const changePage = (data: PageBuffer, newIndex: number, newPageIncrement: number) => {
    updateDetailsTranscripts(data.transcripts);
    updateDetailsLinks({
      hasPreviousPage: data.hasPreviousPage,
      hasNextPage: data.hasNextPage,
    });
    setPreviewTranscriptIndex(newIndex);

    let currentPage = lastNavigatedTranscriptSelectionPage.current[detailsCategory];
    lastNavigatedTranscriptSelectionPage.current = {
      ...lastNavigatedTranscriptSelectionPage.current,
      [detailsCategory]: currentPage + newPageIncrement,
    };
  };

  const handleNavigateToPrevTranscript = () => {
    if (!isFirstTranscriptInCategory) {
      setPreviewTranscriptIndex(previewTranscriptIndex - 1);
      return;
    }

    if (previousPage.current) {
      changePage(previousPage.current, previousPage.current.transcripts.length - 1, -1);
      return;
    }
  };

  const handleNavigateToNextTranscript = () => {
    if (!isLastTranscriptInCategory) {
      setPreviewTranscriptIndex(previewTranscriptIndex + 1);
      return;
    }

    if (nextPage.current) {
      changePage(nextPage.current, 0, 1);
      return;
    }
  };

  return (
    <>
      <TranscriptDetailsHeader data={transcriptDetails} category={detailsCategory} transcript={previewTranscript} />
      <div className="transcript-details-content main-content">
        {transcriptDetails.academic_periods.map(({ period, terms }) => (
          <TranscriptDetailsAccordion key={period} accordionId={transcriptDetails.id} title={period} terms={terms} />
        ))}
      </div>
      <div className="bottom-page-buttons">
        {!isFirstTranscriptInCategory || detailsLinks.hasPreviousPage ? (
          <CustomButton
            variant="secondary"
            buttonText="< Previous Transcript"
            buttonAction={handleNavigateToPrevTranscript}
          />
        ) : null}
        {!isLastTranscriptInCategory || detailsLinks.hasNextPage ? (
          <CustomButton
            variant="primary"
            buttonText="Next Transcript >"
            buttonAction={handleNavigateToNextTranscript}
            sx={{ marginLeft: 'auto' }}
          />
        ) : null}
      </div>
    </>
  );
}

const TranscriptDetailsWithData = () => {
  const { transcriptSelectionSchool } = useSchools();
  const { previewTranscriptIndex, detailsTranscripts, detailsLinks, detailsCategory } =
    useTranscriptSelectionOutletContext();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { lastNavigatedTranscriptSelectionPage } = useTranscriptSelectionPaginationData();

  const previewTranscript = detailsTranscripts[previewTranscriptIndex];

  const previousPage = useRef<PageBuffer | null>(null);
  const nextPage = useRef<PageBuffer | null>(null);

  useEffect(() => {
    if (!userDetails) return;

    // currentPage - 1 = backend index for current page
    let currentPage = lastNavigatedTranscriptSelectionPage.current[detailsCategory];

    if (detailsLinks.hasPreviousPage) {
      getTranscriptSelectionSummary(
        userDetails.eoaId,
        userDetails.access_token,
        transcriptSelectionSchool,
        detailsCategory,
        currentPage - 2
      )
        .then((result) => {
          const linkHeader = result.headers.link;
          let data = {
            transcripts: result.data.transcripts[detailsCategory],
            hasPreviousPage: checkForLink(linkHeader, 'prev'),
            hasNextPage: checkForLink(linkHeader, 'next'),
          };

          previousPage.current = data;
        })
        .catch(() => {
          console.error('Error occured while silently fetching previous page data!');
          previousPage.current = null;
        });
    }

    if (detailsLinks.hasNextPage) {
      getTranscriptSelectionSummary(
        userDetails.eoaId,
        userDetails.access_token,
        transcriptSelectionSchool,
        detailsCategory,
        currentPage
      )
        .then((result) => {
          const linkHeader = result.headers.link;
          let data = {
            transcripts: result.data.transcripts[detailsCategory],
            hasPreviousPage: checkForLink(linkHeader, 'prev'),
            hasNextPage: checkForLink(linkHeader, 'next'),
          };

          nextPage.current = data;
        })
        .catch(() => {
          console.error('Error occured while silently fetching next page data!');
          nextPage.current = null;
        });
    }
  }, [lastNavigatedTranscriptSelectionPage.current[detailsCategory], detailsLinks, userDetails]);

  if (!previewTranscript) return <Navigate to={NAVIGATION_LINKS_DATA.TRANSCRIPTS.path} />;

  return (
    <WithDataWrapper
      fetchData={(eoaId: string, access_token: string) =>
        getTranscriptDetails(eoaId, access_token, transcriptSelectionSchool, previewTranscript.id)
      }
      errorMessage="Transcript details data is unavailable!"
      options={{
        dependencies: [previewTranscript.id],
        areDependenciesRequired: true,
      }}
      initialState={{
        id: '',
        school_student_number: 0,
        category: '',
        academic_periods: [],
        credits: 0,
        credits_earned: 0,
        english_learner: '',
        gender: '',
        gpa: 0,
        is_selected: false,
        low_income: '',
        race: '',
        student_with_disabilities: '',
      }}
    >
      {(data) => (
        <TranscriptDetails
          transcriptDetails={data}
          previewTranscript={previewTranscript}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      )}
    </WithDataWrapper>
  );
};

export default TranscriptDetailsWithData;
